// ─────────────────────────────────────────────────────────────────────────────
// ShopifyBillingGate, Shopify App Pricing plan gate.  (shopify-billing 2026-07-09)
//
// Renders a blocking overlay on the dashboard ONLY for Shopify-App-Store-
// sourced accounts (billing_source='shopify') with no active subscription,
// and sends them to Shopify's HOSTED plan-selection page (App Pricing),
// Shopify runs the entire select/approve/charge flow, per
// docs/SHOPIFY-BILLING-PLAN.md. This is the in-app "billing section" App
// Review 1.2.2 requires: a visible path to select or switch plans.
//
// THE INVARIANT: existing/organic Stripe customers NEVER see this, the
// endpoint returns subscription_required:false for them, and it fails OPEN
// (a billing hiccup must never lock out a legitimate user).
// ─────────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useState } from 'react';
import { fetchShopifyBillingStatus } from '../../lib';

export function ShopifyBillingGate() {
  const [required, setRequired] = useState(false);
  const [pricingUrl, setPricingUrl] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);

  const check = useCallback(async () => {
    setChecking(true);
    try {
      const s = await fetchShopifyBillingStatus();
      setRequired(!!s.subscription_required);
      setPricingUrl(s.pricing_url || null);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => { void check(); }, [check]);

  if (!required) return null;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9000,
        background: 'rgba(14,12,11,0.62)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Choose your Artist CNTRL plan"
    >
      <div style={{ background: '#fff', borderRadius: 20, maxWidth: 520, width: '100%', padding: '34px 36px', boxShadow: '0 30px 80px -20px rgba(14,12,11,0.45)' }}>
        <div style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#8B1A2B', fontWeight: 700 }}>
          Artist CNTRL · Billing
        </div>
        <h2 style={{ fontFamily: "'Archivo Black', sans-serif", fontSize: 30, margin: '10px 0 12px', color: '#0e0c0b' }}>
          Choose your plan
        </h2>
        <p style={{ fontSize: 14.5, lineHeight: 1.6, color: '#4a4642', margin: '0 0 8px' }}>
          {/* monthly-only for now (2026-07-09), annual comes later when the
              listing can carry a second plan; Shopify's page shows whatever
              plans the Partner Dashboard defines, so that's config, not code. */}
          Your Artist CNTRL subscription is billed through <b>Shopify</b>,
          one plan, <b>$150/month</b>.
        </p>
        <p style={{ fontSize: 13, lineHeight: 1.55, color: '#77716b', margin: '0 0 22px' }}>
          You’ll pick and approve the plan on Shopify’s secure billing page, then land
          right back here. Cancel anytime from your Shopify admin.
        </p>
        <a
          href={pricingUrl || '#'}
          style={{
            display: 'block', textAlign: 'center', background: '#8B1A2B', color: '#fff',
            borderRadius: 12, padding: '14px 18px', fontWeight: 700, fontSize: 15,
            textDecoration: 'none', opacity: pricingUrl ? 1 : 0.5, pointerEvents: pricingUrl ? 'auto' : 'none',
          }}
        >
          Choose plan on Shopify →
        </a>
        <button
          type="button"
          onClick={() => void check()}
          disabled={checking}
          style={{
            display: 'block', width: '100%', marginTop: 10, background: 'none', border: 0,
            color: '#77716b', fontSize: 13, padding: 8, cursor: 'pointer', textDecoration: 'underline',
          }}
        >
          {checking ? 'Checking…' : 'I’ve subscribed, refresh'}
        </button>
      </div>
    </div>
  );
}
